import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient';
import { useState } from 'react';
import { Button, useCreate, useListContext, useNotify, useRecordContext, useUnselectAll } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import { LabelSelectionModal } from './label-selection-modal';
import {
  GetLabelsForLineItemByNativeLineItemIdDocument,
  GetLabelsForLineItemByPlacementsLineItemIdDocument,
  Label,
  LineItemLabel,
} from 'src/generated/client';
import {
  LineItemLabelLineItemRecord,
  buildLineItemLabelCreateData,
} from 'src/client/utility/pio-native-lineitem-fk-reads';

const findSelectedLineItemRecord = (
  data: unknown,
  id: string | number,
): LineItemLabelLineItemRecord | string | number => {
  if (Array.isArray(data)) {
    return data.find(record => String(record?.id) === String(id)) ?? id;
  }

  if (data && typeof data === 'object') {
    return (data as Record<string, LineItemLabelLineItemRecord>)[String(id)] ?? id;
  }

  return id;
};

export const AddLineItemLabelButton = () => {
  const notify = useNotify();
  const client = useApolloClient();
  const record = useRecordContext<LineItemLabelLineItemRecord>();
  const { selectedIds, data } = useListContext();
  const lineItems =
    selectedIds && selectedIds.length > 0
      ? selectedIds.map(id => findSelectedLineItemRecord(data, id as string | number))
      : record
      ? [record]
      : [];

  const [selectedLabelId, setSelectedLabelId] = useState<Label['id']>('');
  const [open, setOpen] = useState(false);

  const [create, { isLoading }] = useCreate<LineItemLabel>();
  const unselectAll = useUnselectAll(`campaigns.${record?.id}.line_items`);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleLabelSelect = (labelId: string) => setSelectedLabelId(labelId);
  const refetchLabels = () =>
    client.refetchQueries({
      include: [GetLabelsForLineItemByPlacementsLineItemIdDocument, GetLabelsForLineItemByNativeLineItemIdDocument],
    });

  const handleConfirm = () => {
    lineItems.forEach(lineItem => {
      const data = buildLineItemLabelCreateData({
        lineItem,
        labelId: selectedLabelId,
      });

      if (!data) {
        notify('Error: missing line item id', { type: 'warning' });
        return;
      }

      create(
        'lineItemLabels',
        { data },
        {
          onSuccess: () => {
            notify('Labels assigned successfully');
            unselectAll();
            handleClose();
            refetchLabels();
          },
          onError: error => {
            notify(`Error: ${error}`, { type: 'warning' });
            refetchLabels(); // Refetch even when we fail to insert duplicate label. Remaining labels will still insert correctly.
          },
        },
      );
    });
  };

  return (
    <>
      <Button
        label="Add Line Item Exclusion Label"
        onClick={handleOpen}
        disabled={lineItems.length === 0}
        color="primary"
        startIcon={<AddIcon />}
      />
      <LabelSelectionModal
        open={open}
        selectedLabelId={selectedLabelId}
        onClose={handleClose}
        onLabelSelect={handleLabelSelect}
        onConfirm={handleConfirm}
        confirmDisabled={isLoading || !selectedLabelId}
        title="Add Line Item Exclusion Label"
      />
    </>
  );
};
